import { useState } from 'react';

import { useBulkDeleteFacts } from '@/api/mutations/useBulkDeleteFacts';
import { useBulkUpdateFact } from '@/api/mutations/useBulkUpdateFact';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { RelevanceSelector } from '@/new/components/fact/relevance-selector';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/new/components/shadcn/dialog';
import { Relevance } from '@/types';

interface BatchEditDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedFactCount: number;
  caseId: string;
}

export const BatchEditDialog = ({ isOpen, onOpenChange, selectedFactCount, caseId }: BatchEditDialogProps) => {
  const [selectedRelevance, setSelectedRelevance] = useState<Relevance>(Relevance.YES);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { mutate: updateFacts, isLoading } = useBulkUpdateFact(caseId);

  const { mutate: deleteFacts, isLoading: isDeletingFacts } = useBulkDeleteFacts(caseId);

  const handleConfirm = () => {
    updateFacts(
      { relevance: selectedRelevance },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      },
    );
  };

  const handleDeleteAll = () => {
    deleteFacts(undefined, {
      onSuccess: () => {
        onOpenChange(false);
        setIsDeleteModalOpen(false);
      },
    });
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogTrigger asChild>
          <Button disabled={selectedFactCount === 0} onClick={() => onOpenChange(true)}>
            <Icon className="lucide--edit" />
            Batch Edit
          </Button>
        </DialogTrigger>

        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Icon className="lucide--filter" />
              Batch Edit Facts
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-6">
            <div>
              <p className="text-sm text-gray-800">Update Relevance</p>
              {isLoading ? (
                <div className="flex justify-center p-4 text-gray-500">
                  <Icon className="iconify lucide--loader-circle animate-spin" />
                </div>
              ) : (
                <div className="flex items-end justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2 text-teal-600">
                      <Icon className="lucide--edit" />
                      <p>
                        Update relevance of {selectedFactCount} fact{selectedFactCount === 1 ? '' : 's'} to
                      </p>
                      <RelevanceSelector relevanceText={selectedRelevance} updateRelevance={setSelectedRelevance} />
                    </div>
                  </div>
                  <Button
                    data-testid="confirm-bulk-update-button"
                    variant="primary"
                    onClick={handleConfirm}
                    disabled={isLoading || isDeletingFacts}
                    className="shrink-0"
                  >
                    {isLoading ? 'Updating...' : `Update (${selectedFactCount})`}
                  </Button>
                </div>
              )}
            </div>

            <div className="border-t border-dashed border-gray-200 pt-6">
              <p className="w-fit text-sm text-gray-800">Archive Facts</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Icon className="iconify lucide--archive text-red-600" />
                  <div>
                    <p className="text-sm text-red-600">
                      Archive {selectedFactCount} fact{selectedFactCount === 1 ? '' : 's'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="destructive"
                  onClick={() => setIsDeleteModalOpen(true)}
                  disabled={isLoading || isDeletingFacts || selectedFactCount === 0}
                >
                  Archive ({selectedFactCount})
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        title="Archive Selected Facts"
        onConfirm={handleDeleteAll}
        isLoading={isDeletingFacts}
      >
        <div>
          <p className="mb-3 text-sm text-gray-700">
            Are you sure you want to archive the <strong>{selectedFactCount}</strong> selected fact
            {selectedFactCount === 1 ? '' : 's'}?
          </p>
        </div>
      </ConfirmationModal>
    </>
  );
};
